'use client';

import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { format, parseISO } from 'date-fns';
import { Controller, useFormContext } from 'react-hook-form';

import { cn } from '@/lib/utils';
import type { GeneralFormProps } from '@/types/general';

import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import {
  FormControl,
  FormDescription,
  FormItem,
  FormMessage,
} from '../ui/form';
import { Icon } from '../ui/icon';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const datePickerVariants = cva(
  'peer flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-all duration-200 ease-in-out disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: [
          'border border-border hover:border-action',
          'focus-within:border-2 focus-within:border-action focus:outline-none',
          'data-[placeholder]:text-action',
        ],
        primary: [
          'border border-border hover:border-primary/50',
          'focus-within:border-2 focus-within:border-primary focus:outline-none',
          'data-[placeholder]:text-muted-foreground',
        ],
        secondary: [
          'border border-border hover:border-secondary/50',
          'focus-within:border-2 focus-within:border-secondary focus:outline-none',
          'data-[placeholder]:text-muted-foreground',
        ],
        info: [
          'border border-border hover:border-info/50',
          'focus-within:border-2 focus-within:border-info focus:outline-none',
          'data-[placeholder]:text-muted-foreground',
        ],
        success: [
          'border border-border hover:border-success/50',
          'focus-within:border-2 focus-within:border-success focus:outline-none',
          'data-[placeholder]:text-muted-foreground',
        ],
        warning: [
          'border border-border hover:border-warning/50',
          'focus-within:border-2 focus-within:border-warning focus:outline-none',
          'data-[placeholder]:text-muted-foreground',
        ],
        error: [
          'border border-error hover:border-error',
          'focus-within:border-2 focus-within:border-error focus:outline-none',
          'data-[placeholder]:text-error/60',
        ],
      },
      inputSize: {
        default: 'h-14',
        sm: 'h-11 text-xs',
        lg: 'h-16 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'default',
    },
  },
);

const labelVariants = cva(
  'pointer-events-none absolute left-3 origin-[0] bg-white px-1 text-sm text-muted-foreground transition-all duration-200 ease-in-out',
  {
    variants: {
      variant: {
        default: [
          'peer-focus-within:translate-y-[-26px] peer-focus-within:scale-75 peer-focus-within:text-action-hover',
          'peer-data-[state=open]:translate-y-[-26px] peer-data-[state=open]:scale-75 peer-data-[state=open]:text-action-hover',
          'translate-y-0 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100',
        ],
        primary: [
          'peer-focus-within:translate-y-[-26px] peer-focus-within:scale-75 peer-focus-within:text-primary',
          'peer-data-[state=open]:translate-y-[-26px] peer-data-[state=open]:scale-75 peer-data-[state=open]:text-primary',
          'translate-y-0 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100',
        ],
        secondary: [
          'peer-focus-within:translate-y-[-26px] peer-focus-within:scale-75 peer-focus-within:text-secondary',
          'peer-data-[state=open]:translate-y-[-26px] peer-data-[state=open]:scale-75 peer-data-[state=open]:text-secondary',
          'translate-y-0 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100',
        ],
        info: [
          'peer-focus-within:translate-y-[-26px] peer-focus-within:scale-75 peer-focus-within:text-info',
          'peer-data-[state=open]:translate-y-[-26px] peer-data-[state=open]:scale-75 peer-data-[state=open]:text-info',
          'translate-y-0 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100',
        ],
        success: [
          'peer-focus-within:translate-y-[-26px] peer-focus-within:scale-75 peer-focus-within:text-success',
          'peer-data-[state=open]:translate-y-[-26px] peer-data-[state=open]:scale-75 peer-data-[state=open]:text-success',
          'translate-y-0 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100',
        ],
        warning: [
          'peer-focus-within:translate-y-[-26px] peer-focus-within:scale-75 peer-focus-within:text-warning',
          'peer-data-[state=open]:translate-y-[-26px] peer-data-[state=open]:scale-75 peer-data-[state=open]:text-warning',
          'translate-y-0 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100',
        ],
        error: [
          'peer-focus-within:translate-y-[-26px] peer-focus-within:scale-75 peer-focus-within:text-error',
          'peer-data-[state=open]:translate-y-[-26px] peer-data-[state=open]:scale-75 peer-data-[state=open]:text-error',
          'translate-y-0 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100',
        ],
      },
      inputSize: {
        default: 'top-[16px]',
        sm: 'top-[12px]',
        lg: 'top-[20px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'default',
    },
  },
);

type Props = {
  variant?: VariantProps<typeof datePickerVariants>['variant'];
  inputSize?: VariantProps<typeof datePickerVariants>['inputSize'];
  fromYear?: number;
  toYear?: number;
  startMonth?: Date;
  endMonth?: Date;
  disablePast?: boolean;
  disableFuture?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
} & GeneralFormProps;

function RHFShadcnDatePicker({
  title,
  name,
  description,
  placeholder = 'Pick a date',
  onChange: externalOnChange,
  variant = 'default',
  inputSize = 'default',
  fromYear = 1900,
  toYear = new Date().getFullYear() + 10,
  startMonth,
  endMonth,
  disablePast = false,
  disableFuture = false,
  minDate,
  maxDate,
  disabled = false,
  className,
}: Props) {
  const { control } = useFormContext();

  // Calculate default startMonth and endMonth if not provided
  const defaultStartMonth = startMonth || new Date(fromYear, 0);
  const defaultEndMonth = endMonth || new Date(toYear, 11);

  // Calculate date restrictions
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getDisabledDates = (date: Date) => {
    // Check custom min/max dates first
    if (minDate !== undefined && date < minDate) {
      return true;
    }
    if (maxDate !== undefined && date > maxDate) {
      return true;
    }

    // Check past/future restrictions
    if (disablePast === true && date < today) {
      return true;
    }
    if (disableFuture === true && date > today) {
      return true;
    }

    return false;
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const hasValue = field.value !== undefined && field.value !== null && field.value !== '';

        return (
          <FormItem className={cn('mt-4 flex flex-col w-full', className)}>
            <Popover>
              <div className="relative w-full">
                <PopoverTrigger asChild disabled={disabled}>
                  <FormControl>
                    <Button
                      variant="outline"
                      disabled={disabled}
                      className={cn(
                        datePickerVariants({ variant, inputSize }),
                        'bg-white text-foreground text-left font-medium w-full justify-start hover:bg-white focus-visible:ring-0 focus-visible:ring-offset-0 [&:not(:hover)]:bg-white',
                        disabled === true && 'cursor-not-allowed opacity-50',
                        hasValue === false && 'text-muted-foreground',
                      )}
                    >
                      <span className="flex-1 min-w-0 truncate">
                        {hasValue === true
                          ? (
                              format(parseISO(field.value as string), 'PPP')
                            )
                          : (
                              placeholder
                            )}
                      </span>
                      <Icon
                        icon="material-symbols:calendar-month"
                        className="shrink-0 ml-2"
                        size="xs"
                      />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <label
                  className={cn(
                    labelVariants({ variant, inputSize }),
                    hasValue === true && '-translate-y-[26px] scale-75',
                  )}
                >
                  {title}
                </label>
              </div>
              <PopoverContent className="w-auto p-0 shadow-md rounded-md border" align="start">
                <Calendar
                  mode="single"
                  selected={
                    field.value !== undefined && field.value !== null && field.value !== ''
                      ? parseISO(field.value as string)
                      : undefined
                  }
                  onSelect={(date) => {
                    if (date !== undefined) {
                      // Output ISO 8601 datetime format (consistent with backend API expectations)
                      const formattedDate = format(date, 'yyyy-MM-dd\'T\'HH:mm:ss.SSS\'Z\'');
                      if (externalOnChange !== undefined) {
                        externalOnChange(formattedDate);
                      } else {
                        field.onChange(formattedDate);
                      }
                    }
                  }}
                  disabled={getDisabledDates}
                  captionLayout="dropdown"
                  hideNavigation={true}
                  fromYear={fromYear}
                  toYear={toYear}
                  startMonth={defaultStartMonth}
                  endMonth={defaultEndMonth}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {description !== undefined && (
              <FormDescription>{description}</FormDescription>
            )}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

export default RHFShadcnDatePicker;
